import { PackageCheckIcon } from "lucide-react";
import { use, useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { SearchListButton } from "@/components/canvas-editor/components/ListSearch";
import { StarterPackPreview } from "@/components/canvas-editor/components/StartPackPreview";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  StarterPackShape,
  starterPackType,
  inclusionOperators,
  InclusionOperator,
  StarterPackProps,
  Dimensions,
  inclusionOperatorOptions,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import {
  CustomNodeEditorContext,
  MIN_H,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";


export function isStarterPackShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is StarterPackShape {
  return shape.type === starterPackType;
}

export class StarterPackTool extends ToolStateNode {
  static override id = starterPackType;

  static override uiToolDef = (editor: Editor) => ({
    id: starterPackType,
    icon: PackageCheckIcon,
    label: StarterPackShapeUtil.docs.title,
    defaultColor: StarterPackShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(starterPackType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={StarterPack}
        util={StarterPackShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: starterPackType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class StarterPackShapeUtil extends BindableUtil<StarterPackShape> {
  static override migrations = createMigrations(starterPackType);
  static override type = starterPackType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Starter Pack",
    description:
      "Include or exclude the users in a starter pack from the feed.",
  };

  override getType() {
    return starterPackType;
  }

  static override props: RecordProps<StarterPackShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    starterPackUrl: T.string,
    operator: T.literalEnum(...inclusionOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 500,
      minW: 500,
      h: MIN_H,
      starterPackUrl:
        "https://bsky.app/starter-pack/propublica.org/3l6iflmcj322n",
      operator: inclusionOperators[0],
      metadata: undefined,
    };
  }

  override component(shape: StarterPackShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <StarterPack
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function StarterPack({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: StarterPackShape;
  className?: string;
  updateShape: (
    shape: StarterPackShape,
    props: Partial<StarterPackProps>,
  ) => void;
}) {
  const isCustomNodeEditor = use(CustomNodeEditorContext);
  const isReadonly = useIsReadonlyEditor() || shape.props.isReadOnly;
  const variant = shape.props.operator === "in" ? "green" : "red";

  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={{
            ...StarterPackShapeUtil.docs,
            title: (
              <>
                {isReadonly &&
                  (shape.props.operator === "in" ? "In " : "Not in ")}
                {StarterPackShapeUtil.docs.title}
              </>
            ),
          }}
        />
        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {!isReadonly && (
              <div className="flex gap-2">
                <CanvasSimpleSelect
                  aria-label="Inclusion Operator"
                  className="w-28"
                  value={shape.props.operator}
                  onValueChange={(v) =>
                    updateShape(shape, { operator: v as InclusionOperator })
                  }
                  options={inclusionOperatorOptions}
                />
                <SimpleTooltip message="The starter pack URL">
                  <CanvasInput
                    aria-label="Starter Pack URL"
                    className="flex-1"
                    value={shape.props.starterPackUrl}
                    placeholder="Starter Pack URL"
                    onValueChange={(starterPackUrl) =>
                      updateShape(shape, { starterPackUrl })
                    }
                    onInput={(e) => {
                      if (
                        e.currentTarget.value.match(
                          /https:\S+\/starter-pack\/\S+\/\S+$/,
                        )
                      ) {
                        e.currentTarget.setCustomValidity("");
                      } else if (!isCustomNodeEditor) {
                        e.currentTarget.setCustomValidity(
                          "Please enter a valid starter pack URL.",
                        );
                        e.currentTarget.reportValidity();
                      }
                    }}
                  />
                </SimpleTooltip>
                <SearchListButton
                  type="starter-pack"
                  selectUrl={(url) => {
                    updateShape(shape, { starterPackUrl: url });
                  }}
                />
              </div>
            )}

            <StarterPackPreview
              starterPackUrl={shape.props.starterPackUrl}
              hasSeparator={!isReadonly}
            />
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
